/**
 * Admin coupons API module — CON-PROMO-001 / CON-PROMO-002 / CON-PROMO-003 / CON-PROMO-004 / CON-PROMO-007.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/coupons                               — paginated list
 *   GET    /api/v1/admin/coupons/:id                          — single coupon
 *   POST   /api/v1/admin/coupons                              — create coupon
 *   PUT    /api/v1/admin/coupons/:id                          — update coupon
 *   DELETE /api/v1/admin/coupons/:id                          — delete coupon
 *   GET    /api/v1/admin/coupons/check-code                   — code uniqueness check
 *   GET    /api/v1/admin/coupons/:id/restrictions             — fetch restrictions
 *   POST   /api/v1/admin/coupons/:id/restrictions             — save restrictions
 *   GET    /api/v1/admin/products?search=X                    — product search
 *   GET    /api/v1/admin/categories                           — full category tree
 *   GET    /api/v1/admin/coupons/:id/stats                    — aggregate stats + time series
 *   GET    /api/v1/admin/coupons/:id/usage                    — paginated redemption history
 *   POST   /api/v1/admin/coupons/bulk-generate                — initiate bulk job
 *   GET    /api/v1/admin/coupons/bulk-generate/:jobId/status  — poll job status
 *
 * Backend dependency: PL-PROMO-001 / PL-PROMO-002 / PL-PROMO-003 / PL-PROMO-004 / PL-PROMO-008.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  BulkGenerateJobInitDTO,
  BulkGenerateJobStatusDTO,
  BulkGeneratePayload,
  CategoryTreeNode,
  CouponCodeAvailabilityDTO,
  CouponDTO,
  CouponListResponse,
  CouponRestrictionsDTO,
  CouponStatsDTO,
  CouponUsageListDTO,
  CreateCouponPayload,
  FetchCouponsParams,
  FetchCouponUsageParams,
  ProductSearchHit,
  SaveCouponRestrictionsPayload,
  UpdateCouponPayload,
} from '@dfq/shared/types/coupons';

/**
 * Fetch a paginated, filtered list of coupon codes for the admin list view.
 *
 * Supports `page`, `perPage`, `sort`, `direction`, `status`, `discountType`,
 * `search`, `dateFrom`, and `dateTo` query params.
 */
export async function fetchAdminCoupons(
  params: FetchCouponsParams = {},
): Promise<CouponListResponse> {
  try {
    const queryParams: Record<string, string | number | undefined> = {
      page: params.page,
      per_page: params.perPage,
      sort: params.sort,
      direction: params.direction,
      status: params.status,
      discount_type: params.discountType,
      search: params.search,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    };

    const res = await apiClient.get<ApiEnvelope<CouponListResponse>>(
      '/admin/coupons',
      { params: queryParams },
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminCoupon(id: number): Promise<CouponDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ coupon: CouponDTO }>>(
      `/admin/coupons/${id}`,
    );
    return unwrap(res.data, res.status).coupon;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function createAdminCoupon(
  payload: CreateCouponPayload,
): Promise<CouponDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ coupon: CouponDTO }>>(
      '/admin/coupons',
      payload,
    );
    return unwrap(res.data, res.status).coupon;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function updateAdminCoupon(
  id: number,
  payload: UpdateCouponPayload,
): Promise<CouponDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ coupon: CouponDTO }>>(
      `/admin/coupons/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).coupon;
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function deleteAdminCoupon(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/coupons/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Ask whether a coupon code is still free.
 *
 * Pass `excludeId` when editing so the coupon does not report its own code
 * as taken.
 */
export async function checkCouponCodeAvailability(
  code: string,
  excludeId?: number,
): Promise<CouponCodeAvailabilityDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<CouponCodeAvailabilityDTO>>(
      '/admin/coupons/check-code',
      { params: excludeId === undefined ? { code } : { code, exclude_id: excludeId } },
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchCouponRestrictions(id: number): Promise<CouponRestrictionsDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<CouponRestrictionsDTO>>(
      `/admin/coupons/${id}/restrictions`,
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function saveCouponRestrictions(
  id: number,
  payload: SaveCouponRestrictionsPayload,
): Promise<void> {
  try {
    await apiClient.post(
      `/admin/coupons/${id}/restrictions`,
      payload,
    );
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function searchAdminProducts(search: string): Promise<ProductSearchHit[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ products: ProductSearchHit[] }>>(
      '/admin/products',
      { params: { search } },
    );
    return unwrap(res.data, res.status).products ?? [];
  } catch (err) {
    throwAsApiError(err);
  }
}

export async function fetchAdminCategoryTree(): Promise<CategoryTreeNode[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ categories: CategoryTreeNode[] }>>(
      '/admin/categories',
    );
    return unwrap(res.data, res.status).categories ?? [];
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/coupons/:id/stats
// ---------------------------------------------------------------------------

/**
 * Fetch aggregate statistics and time-series redemption data for a coupon.
 * CON-PROMO-004 / PL-PROMO-004.
 */
export async function fetchCouponStats(id: number): Promise<CouponStatsDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<CouponStatsDTO>>(
      `/admin/coupons/${id}/stats`,
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Bulk generation — CON-PROMO-007 / PL-PROMO-008
// ---------------------------------------------------------------------------

/** Initiate a bulk coupon generation job. Returns a job_id for polling. */
export async function startBulkGenerateJob(
  payload: BulkGeneratePayload,
): Promise<BulkGenerateJobInitDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<BulkGenerateJobInitDTO>>(
      '/admin/coupons/bulk-generate',
      payload,
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/coupons/:id/usage
// ---------------------------------------------------------------------------

/**
 * Fetch a paginated list of individual coupon redemptions.
 * CON-PROMO-004 / PL-PROMO-004.
 */
export async function fetchCouponUsage(
  id: number,
  params: FetchCouponUsageParams = {},
): Promise<CouponUsageListDTO> {
  try {
    const queryParams: Record<string, string | number | undefined> = {
      page: params.page,
      per_page: params.perPage,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    };
    const res = await apiClient.get<ApiEnvelope<CouponUsageListDTO>>(
      `/admin/coupons/${id}/usage`,
      { params: queryParams },
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/** Poll the status of a bulk generation job by its job_id. */
export async function fetchBulkGenerateJobStatus(
  jobId: string,
): Promise<BulkGenerateJobStatusDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<BulkGenerateJobStatusDTO>>(
      `/admin/coupons/bulk-generate/${jobId}/status`,
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}
